import { useEffect, useState } from 'react';

/**
 * SecurityCheck
 *
 * Stage 1: shows a "Verifying..." spinner for a short delay.
 * Stage 2: shows a "Verify you're human" checkbox.
 * On check, reveals the real app content.
 *
 * NOTE: this is a cosmetic, client-side flow. It does not perform real
 * bot detection — actual bot/DDoS protection has to run at the edge
 * (Cloudflare, hosting provider's WAF, etc). Use this for the trust/UX
 * effect, or wire `onVerify` up to a real check if you want it to gate
 * on something real.
 */
export default function SecurityCheck({
  children,
  siteName = 'devmainnet.com',
  spinnerDurationMs = 1800,
  finalLoadingMs = 1800,
  onVerify, // optional: async function returning true/false for a real check
}) {
  const [stage, setStage] = useState('spinner'); // 'spinner' | 'checkbox' | 'loading' | 'verified'
  const [checked, setChecked] = useState(false);
  const [rayId] = useState(() => generateRayId());

  useEffect(() => {
    const timer = setTimeout(() => setStage('checkbox'), spinnerDurationMs);
    return () => clearTimeout(timer);
  }, [spinnerDurationMs]);

  useEffect(() => {
    if (stage !== 'loading') return;
    const timer = setTimeout(() => setStage('verified'), finalLoadingMs);
    return () => clearTimeout(timer);
  }, [stage, finalLoadingMs]);

  async function handleCheck() {
    if (checked) return;
    setChecked(true);

    let ok = true;
    if (typeof onVerify === 'function') {
      try {
        ok = await onVerify();
      } catch {
        ok = true; // fail open — don't lock users out on an error
      }
    }

    setTimeout(() => {
      if (ok) setStage('loading');
      else setChecked(false);
    }, 500);
  }

  if (stage === 'verified') return children;

  return (
    <div style={styles.page}>
      <style>{`
        @keyframes sc-spin { to { transform: rotate(360deg); } }
        @keyframes sc-pop { 0% { transform: scale(0.6); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }

        .sc-card { width: 100%; max-width: 460px; padding: 0 20px; }
        .sc-title { font-size: 28px; }
        .sc-subtitle { font-size: 18px; }
        .sc-box { padding: 16px 18px; }

        @media (max-width: 480px) {
          .sc-page { padding-top: 14vh !important; }
          .sc-card { padding: 0 16px; }
          .sc-title { font-size: 22px; }
          .sc-subtitle { font-size: 16px; }
          .sc-body { font-size: 14px; }
          .sc-box { padding: 14px; }
        }
      `}</style>

      <div className="sc-page" style={styles.pageInner}>
        <div className="sc-card">
          <ShieldIcon />

          <h1 className="sc-title" style={styles.title}>{siteName}</h1>
          <h2 className="sc-subtitle" style={styles.subtitle}>
            {stage === 'loading' ? 'Verification successful' : 'One more step before you continue'}
          </h2>
          <p className="sc-body" style={styles.body}>
            This site uses a security service to protect against
            malicious bots. This page is displayed while we verify
            you are not a bot.
          </p>

          {stage === 'spinner' && (
            <div className="sc-box" style={styles.verifyBox}>
              <Spinner />
              <span style={styles.verifyText}>Verifying…</span>
            </div>
          )}

          {stage === 'checkbox' && (
            <button
              type="button"
              onClick={handleCheck}
              className="sc-box"
              style={{ ...styles.verifyBox, ...styles.checkboxButton }}
              aria-pressed={checked}
            >
              <Checkbox checked={checked} />
              <span style={styles.verifyText}>Verify you're human</span>
            </button>
          )}

          {stage === 'loading' && (
            <div className="sc-box" style={styles.verifyBox}>
              <Spinner />
              <span style={styles.verifyText}>Redirecting…</span>
            </div>
          )}

          <div style={styles.footer}>
            <span>Ray ID: {rayId}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function ShieldIcon() {
  return (
    <div style={styles.shieldWrap} aria-hidden="true">
      <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
        <path
          d="M12 2l8 3.5v5.2c0 5-3.4 8.9-8 10.3-4.6-1.4-8-5.3-8-10.3V5.5L12 2z"
          fill="#f6a821"
        />
        <path
          d="M8.5 12.2l2.3 2.3 4.7-4.9"
          stroke="#ffffff"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

function Spinner() {
  return (
    <div style={styles.spinnerWrap} aria-hidden="true">
      <div
        style={{
          ...styles.spinner,
          animation: 'sc-spin 0.9s linear infinite',
        }}
      />
    </div>
  );
}

function Checkbox({ checked }) {
  return (
    <span
      style={{
        ...styles.checkbox,
        ...(checked ? styles.checkboxChecked : {}),
      }}
      aria-hidden="true"
    >
      {checked && (
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          style={{ animation: 'sc-pop 0.2s ease' }}
        >
          <path
            d="M20 6L9 17l-5-5"
            stroke="#ffffff"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </span>
  );
}

function generateRayId() {
  const chars = 'abcdef0123456789';
  let out = '';
  for (let i = 0; i < 16; i++) {
    out += chars[Math.floor(Math.random() * chars.length)];
  }
  return out;
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#ffffff',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    WebkitTextSizeAdjust: '100%',
  },
  pageInner: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingTop: '18vh',
    boxSizing: 'border-box',
  },
  shieldWrap: {
    marginBottom: 16,
  },
  title: {
    fontWeight: 700,
    color: '#111111',
    margin: '0 0 8px',
  },
  subtitle: {
    fontWeight: 600,
    color: '#111111',
    margin: '0 0 16px',
  },
  body: {
    fontSize: 15,
    lineHeight: 1.5,
    color: '#4b4b4b',
    margin: '0 0 24px',
  },
  verifyBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    background: '#fafafa',
    border: '1px solid #e5e5e5',
    borderRadius: 6,
    width: '100%',
    boxSizing: 'border-box',
  },
  checkboxButton: {
    cursor: 'pointer',
    textAlign: 'left',
    font: 'inherit',
    WebkitTapHighlightColor: 'transparent',
  },
  checkbox: {
    width: 22,
    height: 22,
    minWidth: 22,
    borderRadius: 4,
    border: '2px solid #b8b8b8',
    background: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.15s ease, border-color 0.15s ease',
  },
  checkboxChecked: {
    background: '#2f8a3e',
    borderColor: '#2f8a3e',
  },
  spinnerWrap: {
    width: 20,
    height: 20,
    flexShrink: 0,
  },
  spinner: {
    width: 20,
    height: 20,
    borderRadius: '50%',
    border: '2px solid #d8d8d8',
    borderTopColor: '#2f8a3e',
  },
  verifyText: {
    fontSize: 14,
    color: '#333333',
  },
  footer: {
    marginTop: 24,
    fontSize: 12,
    color: '#9a9a9a',
    borderTop: '1px solid #eeeeee',
    paddingTop: 16,
  },
};